import axios from "axios";
import React, { useEffect, useRef, useState } from "react";

import {
  ArrowLeft,
  ArrowRight,
  Bell,
  BriefcaseBusiness,
  Building2,
  Check,
  CheckCircle2,
  ChevronDown,
  Clock3,
  Eye,
  EyeOff,
  FileText,
  Globe2,
  HeartHandshake,
  Home,
  Lightbulb,
  LockKeyhole,
  LogIn,
  LogOut,
  Mail,
  MapPin,
  Menu,
  MessageCircle,
  MessageSquare,
  Phone,
  Search,
  ShieldCheck,
  Smartphone,
  UserCircle,
  UserRound,
  Users,
  X,
} from "lucide-react";
import AdminConsole from "./AdminConsole";
import AuthorPage from "./AuthorPage";
import LeafletMap from "./LeafletMap";
import {
  VIJAYANAGAR_WARDS,
  CIVIC_CATEGORIES,
  CIVIC_CATEGORIES_KN,
  wardSlug,
} from "./wardData";
import "@/App.css";
import "@/AppExtras.css";

const BACKEND = process.env.REACT_APP_BACKEND_URL;




const SEO_DEFAULT = {
  title: "Vijayanagar Civic Portal – Report & Track Civic Issues",
  description:
    "Vijayanagar Civic Portal for residents to report and track civic issues including potholes, roads, water supply, garbage, drainage, street lights, sanitation, parks and other local civic services.",
};

const SEO_ROUTES = {
  "/": {
    title: "Vijayanagar Civic Portal – Report & Track Civic Issues",
    description:
      "Report and track civic issues in Vijayanagar including potholes, damaged roads, water supply, garbage, drainage, street lights, sanitation and other local civic problems.",
    index: true,
  },

  "/author": {
    title: "About Vijayanagar Civic Portal",
    description:
      "Learn about the Vijayanagar Civic Portal, its purpose, civic services and digital tools for residents to report and follow local civic issues.",
    index: true,
  },

  "/wards": {
    title: "Vijayanagar Wards – Civic Information & Local Issues",
    description:
      "Explore Vijayanagar wards and view local civic information, reported issues and ward-level civic activity.",
    index: true,
  },

  "/dashboard": {
    title: "Vijayanagar Civic Dashboard – Civic Issues & Services",
    description:
      "View public civic activity, reported issues, resolution information and civic service statistics for Vijayanagar.",
    index: true,
  },

  "/services": {
    title: "Vijayanagar Civic Services – Local Services & Information",
    description:
      "Find civic and community services available to residents in Vijayanagar.",
    index: true,
  },

  "/volunteer": {
    title: "Volunteer & Civic Leadership | Vijayanagar",
    description: "Join a civic volunteer initiative in Vijayanagar. Learn leadership, support community service and help residents access civic information."
  },
  "/community": {
    title: "Vijayanagar Community – Local Civic Information",
    description:
      "Explore community information, civic suggestions and local resources for residents of Vijayanagar.",
    index: true,
  },

  "/jobs": {
    title: "Vijayanagar Jobs – Local Job Opportunities",
    description:
      "Browse local employment opportunities and job listings available through the Vijayanagar Civic Portal.",
    index: true,
  },

  "/contact": {
    title: "Contact Vijayanagar Civic Portal",
    description:
      "Contact the Vijayanagar Civic Portal for help with civic services, reporting issues, complaint tracking and portal assistance.",
    index: true,
  },

  "/admin": {
    title: "Admin – Vijayanagar Civic Portal",
    description: "",
    index: false,
  },

  "/profile": {
    title: "Resident Profile – Vijayanagar Civic Portal",
    description: "",
    index: false,
  },

  "/reports": {
    title: "My Reports – Vijayanagar Civic Portal",
    description: "",
    index: false,
  },

  "/reset-password": {
    title: "Reset Password – Vijayanagar Civic Portal",
    description: "",
    index: false,
  },

  "/post-job": {
    title: "Post a Job – Vijayanagar Civic Portal",
    description:
      "Submit a local job vacancy for review through the Vijayanagar Civic Portal.",
    index: false,
  },
};

function updateSEO(route) {
  const cleanRoute = route.split("?")[0].split("#")[0];

  let seo = SEO_ROUTES[cleanRoute];

  if (!seo && /^\/ward\/[^/]+$/.test(cleanRoute)) {
    const slug = cleanRoute
      .split("/")
      .filter(Boolean)
      .pop()
      .replaceAll("-", " ");

    const wardName = slug
      .replace(/\b\w/g, (char) => char.toUpperCase());

    seo = {
      title: `${wardName} – Vijayanagar Ward Civic Information`,
      description:
        `View civic information, local issues and public civic activity for ${wardName} in Vijayanagar.`,
      index: true,
    };
  }

  seo = seo || SEO_DEFAULT;

  document.title = seo.title;

  const setMeta = (selector, content) => {
    let element = document.head.querySelector(selector);

    if (!element) {
      element = document.createElement("meta");

      if (selector.includes('name="description"')) {
        element.setAttribute("name", "description");
      } else if (selector.includes('name="robots"')) {
        element.setAttribute("name", "robots");
      }

      document.head.appendChild(element);
    }

    element.setAttribute("content", content);
  };

  setMeta(
    'meta[name="description"]',
    seo.description
  );

  setMeta(
    'meta[name="robots"]',
    seo.index === false
      ? "noindex, nofollow, noarchive"
      : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
  );

  let canonical = document.head.querySelector(
    'link[rel="canonical"]'
  );

  if (!canonical) {
    canonical = document.createElement("link");
    canonical.setAttribute("rel", "canonical");
    document.head.appendChild(canonical);
  }

  canonical.setAttribute(
    "href",
    `https://vijayanagar.org${cleanRoute === "/" ? "/" : cleanRoute}`
  );

  const setOG = (property, content) => {
    let element = document.head.querySelector(
      `meta[property="${property}"]`
    );

    if (!element) {
      element = document.createElement("meta");
      element.setAttribute("property", property);
      document.head.appendChild(element);
    }

    element.setAttribute("content", content);
  };

  setOG("og:title", seo.title);
  setOG("og:description", seo.description);
  setOG(
    "og:url",
    `https://vijayanagar.org${cleanRoute === "/" ? "/" : cleanRoute}`
  );

  setOG(
    "og:type",
    "website"
  );
}

const api = axios.create({
  baseURL: `${BACKEND}/api`,
  withCredentials: true,
});

const copy = {
  en: {
    home: "Home",
    report: "Report an Issue",
    track: "Track Complaint",
    wards: "Explore Wards",
    dashboard: "Civic Dashboard",
    services: "Services",
    community: "Community",
    jobs: "Jobs",
    login: "Resident Login",
    myReports: "My Reports",
    profile: "Profile",
    logout: "Logout",
    reportNow: "Report a Civic Problem",
    trackNow: "Track My Complaint",
    exploreWard: "Explore My Ward",
    dashboardBtn: "Civic Dashboard",
    reported: "Issues Reported",
    progress: "In Progress",
    resolved: "Resolved",
    servicesCount: "Civic Services",
    reportTitle: "Report a Civic Issue",
    title: "Issue title",
    category: "Category",
    subCategory: "Sub-category",
    ward: "Ward",
    description: "Describe the problem",
    address: "Address",
    landmark: "Landmark",
    priority: "Priority",
    photo: "Photos",
    location: "Location",
    getLocation: "Use My Location",
    captured: "Location captured",
    submit: "Submit Complaint",
    loginRequired: "Sign in to report an issue",
    success: "Complaint Submitted Successfully",
    trackingId: "Tracking ID",
    status: "Status",
    timeline: "Complaint Timeline",
    noResult: "Complaint not found.",
    feedback: "Citizen Feedback",
    reopen: "Reopen Complaint",
    resolvedQuestion: "Was the issue actually fixed?",
    yes: "Yes",
    partial: "Partially",
    no: "No",
    save: "Save",
    close: "Close",
  },
  kn: {
    home: "ಮುಖ್ಯಪುಟ",
    report: "ಸಮಸ್ಯೆ ವರದಿ ಮಾಡಿ",
    track: "ದೂರು ಟ್ರ್ಯಾಕ್ ಮಾಡಿ",
    wards: "ವಾರ್ಡ್‌ಗಳು",
    dashboard: "ನಾಗರಿಕ ಡ್ಯಾಶ್‌ಬೋರ್ಡ್",
    services: "ಸೇವೆಗಳು",
    community: "ಸಮುದಾಯ",
    jobs: "ಉದ್ಯೋಗಗಳು",
    login: "ನಿವಾಸಿ ಲಾಗಿನ್",
    myReports: "ನನ್ನ ದೂರುಗಳು",
    profile: "ಪ್ರೊಫೈಲ್",
    logout: "ಲಾಗ್‌ಔಟ್",
    reportNow: "ನಾಗರಿಕ ಸಮಸ್ಯೆ ವರದಿ ಮಾಡಿ",
    trackNow: "ನನ್ನ ದೂರು ಟ್ರ್ಯಾಕ್ ಮಾಡಿ",
    exploreWard: "ನನ್ನ ವಾರ್ಡ್ ನೋಡಿ",
    dashboardBtn: "ನಾಗರಿಕ ಡ್ಯಾಶ್‌ಬೋರ್ಡ್",
    reported: "ವರದಿಯಾದ ಸಮಸ್ಯೆಗಳು",
    progress: "ಪ್ರಗತಿಯಲ್ಲಿವೆ",
    resolved: "ಪರಿಹಾರವಾದವು",
    servicesCount: "ನಾಗರಿಕ ಸೇವೆಗಳು",
    reportTitle: "ನಾಗರಿಕ ಸಮಸ್ಯೆ ವರದಿ ಮಾಡಿ",
    title: "ಸಮಸ್ಯೆಯ ಶೀರ್ಷಿಕೆ",
    category: "ವಿಭಾಗ",
    subCategory: "ಉಪ-ವಿಭಾಗ",
    ward: "ವಾರ್ಡ್",
    description: "ಸಮಸ್ಯೆಯನ್ನು ವಿವರಿಸಿ",
    address: "ವಿಳಾಸ",
    landmark: "ಗುರುತು",
    priority: "ಆದ್ಯತೆ",
    photo: "ಫೋಟೋಗಳು",
    location: "ಸ್ಥಳ",
    getLocation: "ನನ್ನ ಸ್ಥಳ ಬಳಸಿ",
    captured: "ಸ್ಥಳ ದಾಖಲಿಸಲಾಗಿದೆ",
    submit: "ದೂರು ಸಲ್ಲಿಸಿ",
    loginRequired: "ದೂರು ಸಲ್ಲಿಸಲು ಮೊದಲು ಲಾಗಿನ್ ಮಾಡಿ.",
    success: "ದೂರು ಯಶಸ್ವಿಯಾಗಿ ಸಲ್ಲಿಸಲಾಗಿದೆ",
    trackingId: "ಟ್ರ್ಯಾಕಿಂಗ್ ID",
    status: "ಸ್ಥಿತಿ",
    timeline: "ದೂರು ಸಮಯರೇಖೆ",
    noResult: "ದೂರು ಕಂಡುಬಂದಿಲ್ಲ.",
    feedback: "ನಾಗರಿಕ ಪ್ರತಿಕ್ರಿಯೆ",
    reopen: "ದೂರು ಮರುತೆರೆಯಿರಿ",
    resolvedQuestion: "ಸಮಸ್ಯೆ ನಿಜವಾಗಿಯೂ ಪರಿಹಾರವಾಗಿದೆಯೇ?",
    yes: "ಹೌದು",
    partial: "ಭಾಗಶಃ",
    no: "ಇಲ್ಲ",
    save: "ಉಳಿಸಿ",
    close: "ಮುಚ್ಚಿ",
  },
};

const subCategories = {
  Roads: ["Pothole", "Damaged Road", "Footpath", "Road Marking"],
  Water: ["No Supply", "Leakage", "Low Pressure", "Contamination"],
  Waste: ["Garbage", "Overflowing Bin", "Illegal Dumping"],
  "Street Lights": ["Not Working", "Dark Area", "Damaged Pole"],
  Drainage: ["Blocked Drain", "Sewage Overflow", "Flooding"],
  Parks: ["Maintenance", "Tree", "Playground"],
  Schools: ["Infrastructure", "Toilet", "Water", "Safety"],
  Health: ["Facility", "Sanitation", "Equipment"],
  Traffic: ["Signal", "Road Safety", "Marking", "Parking"],
  "Animal Issues": ["Stray Animals", "Animal Waste", "Other"],
  Encroachment: ["Public Space", "Footpath", "Road"],
  Sanitation: ["Cleaning", "Public Hygiene", "Other"],
  Other: ["Other"],
};

const wardPath = (ward) => `/ward/${wardSlug(ward.name)}`;

export default function App() {
  const [lang, setLang] = useState("en");
  const [route, setRoute] = useState(window.location.pathname);
  useEffect(() => {
    updateSEO(route);
  }, [route]);

  const [user, setUser] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [modal, setModal] = useState(null);
  const [mobile, setMobile] = useState(false);
  const [openMenu, setOpenMenu] = useState("");

  const t = copy[lang];

  useEffect(() => {
    api.get("/dashboard").then((r) => setDashboard(r.data)).catch(() => {});
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => {});

    const pop = () => setRoute(window.location.pathname);
    window.addEventListener("popstate", pop);

    return () => window.removeEventListener("popstate", pop);
  }, []);

  const go = (path) => {
    window.history.pushState({}, "", path);
    setRoute(path);
    setMobile(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    setUser(null);
    go("/");
  };

  if (route === "/reset-password") {
    return <ResetPasswordPage onBack={() => go("/")} go={go} />;
  }

  if (route === "/admin") {
    if (user?.role === "admin" || user?.role === "staff") {
      return <AdminConsole user={user} onBack={() => go("/")} />;
    }

    return (
      <div className="civic-gate">
        <ShieldCheck size={42} />
        <h1>Admin access required</h1>
        <button className="civic-btn primary" onClick={() => go("/")}>
          {t.home}
        </button>
      </div>
    );
  }

  if (route === "/author") {
    return <AuthorPage />;
  }

  if (route === "/reports") {
    return user ? (
      <MyReports user={user} t={t} onBack={() => go("/")} />
    ) : (
      <Gate t={t} onLogin={() => setModal("login")} onBack={() => go("/")} />
    );
  }

  if (route === "/profile") {
    return user ? (
      <ResidentProfile user={user} t={t} lang={lang} onBack={() => go("/")} />
    ) : (
      <Gate t={t} onLogin={() => setModal("login")} onBack={() => go("/")} />
    );
  }

  if (route === "/wards") {
    return <WardsPage t={t} onBack={() => go("/")} onWard={(w) => go(wardPath(w))} />;
  }

  if (route === "/dashboard") {
    return <DashboardPage t={t} onBack={() => go("/")} />;
  }

  if (route === "/services") {
    return <PublicServicesPage onBack={() => go("/")} go={go} />;
  }

  if (/^\/services\/(temples|hospitals|police-stations|libraries|schools)$/.test(route)) {
    const category = route.split("/").pop();

    return (
      <PublicServiceCategoryPage
        category={category}
        onBack={() => go("/services")}
      />
    );
  }

  if (route === "/community") {
    return <CommunityPage user={user} t={t} onBack={() => go("/")} onLogin={() => setModal("login")} />;
  }

  if (route === "/volunteer") {
    return <VolunteerPage onBack={() => go("/")} go={go} />;
  }

  if (route === "/contact") {
    return <ContactPage onBack={() => go("/")} />;
  }

  if (route === "/post-job") {
    return <EmployerJobPage onBack={() => go("/")} onJobs={() => go("/jobs")} />;
  }

  if (route === "/jobs") {
    return <JobsBoard onBack={() => go("/")} onPostJob={() => go("/post-job")} />;
  }

  const wardMatch = route.match(/^\/ward\/([^/]+)$/);

  if (wardMatch) {
    const ward = VIJAYANAGAR_WARDS.find(
      (w) => wardSlug(w.name) === wardMatch[1].toLowerCase()
    );

    if (!ward) return <NotFound onBack={() => go("/")} />;

    return (
      <div className="civic-site" lang={lang === "kn" ? "kn" : "en"}>
        <WardPage
          ward={ward}
          t={t}
          user={user}
          setUser={setUser}
          onBack={() => go("/")}
        />

        {modal && (
          <Modal
            type={modal}
            user={user}
            setUser={setUser}
            close={() => setModal(null)}
            onProfile={() => {
              setModal(null);
              go("/profile");
            }}
            t={t}
            lang={lang}
          />
        )}
      </div>
    );
  }

  return (
    <div className="civic-site" lang={lang === "kn" ? "kn" : "en"}>
      <header className="civic-header">
        <div className="civic-header-inner">

          <button className="civic-brand brand-button" onClick={() => go("/")}>
            <span className="civic-logo">V</span>
            <span className="civic-brand-text">
              <strong>Vijayanagar</strong>
              <small>Civic Portal</small>
            </span>
          </button>

          <nav className={mobile ? "civic-nav open" : "civic-nav"}>

  {/* 1. HOME */}
  <button
    className="nav-link"
    onClick={() => {
      go("/");
      setOpenMenu("");
      setMobile(false);
    }}
  >
    {t.home}
  </button>

  {/* 2. ABOUT */}
  <button
    className="nav-link"
    onClick={() => {
      go("/author");
      setOpenMenu("");
      setMobile(false);
    }}
  >
    About
  </button>

  {/* 3. REPORT ISSUE + TRACK COMPLAINT */}
  <div className="nav-dropdown">
    <button
      className="nav-report dropdown-trigger"
      onClick={(e) => {
        e.stopPropagation();
        setOpenMenu(openMenu === "report" ? "" : "report");
      }}
      aria-expanded={openMenu === "report"}
    >
      <FileText size={16} />
      <span>Report Issue</span>
      <ChevronDown size={15} />
    </button>

    <div
      className={
        openMenu === "report"
          ? "dropdown-menu show"
          : "dropdown-menu"
      }
    >
      <button
        onClick={() => {
          setModal("report");
          setOpenMenu("");
          setMobile(false);
        }}
      >
        <FileText size={15} />
        Report an Issue
      </button>

      <button
        onClick={() => {
          setModal("track");
          setOpenMenu("");
          setMobile(false);
        }}
      >
        <Search size={15} />
        Track Complaint
      </button>

      {user && (
        <button
          onClick={() => {
            go("/reports");
            setOpenMenu("");
            setMobile(false);
          }}
        >
          <FileText size={15} />
          My Reports
        </button>
      )}
    </div>
  </div>

  {/* 4. EXPLORE */}
  <div className="nav-dropdown">
    <button
      className="nav-link dropdown-trigger"
      onClick={(e) => {
        e.stopPropagation();
        setOpenMenu(openMenu === "explore" ? "" : "explore");
      }}
      aria-expanded={openMenu === "explore"}
    >
      <span>Explore</span>
      <ChevronDown size={15} />
    </button>

    <div
      className={
        openMenu === "explore"
          ? "dropdown-menu show"
          : "dropdown-menu"
      }
    >
      <button
        onClick={() => {
          go("/wards");
          setOpenMenu("");
          setMobile(false);
        }}
      >
        <MapPin size={15} />
        Explore Wards
      </button>

      <button
        onClick={() => {
          go("/dashboard");
          setOpenMenu("");
          setMobile(false);
        }}
      >
        <ShieldCheck size={15} />
        Civic Dashboard
      </button>

      <button
        onClick={() => {
          go("/services");
          setOpenMenu("");
          setMobile(false);
        }}
      >
        <Building2 size={15} />
        Civic Services
      </button>
    </div>
  </div>

  {/* 5. COMMUNITY */}
  <div className="nav-dropdown">
    <button
      className="nav-link dropdown-trigger"
      onClick={(e) => {
        e.stopPropagation();
        setOpenMenu(openMenu === "community" ? "" : "community");
      }}
      aria-expanded={openMenu === "community"}
    >
      <span>Community</span>
      <ChevronDown size={15} />
    </button>

    <div
      className={
        openMenu === "community"
          ? "dropdown-menu show"
          : "dropdown-menu"
      }
    >
      <button
        onClick={() => {
          go("/community");
          setOpenMenu("");
          setMobile(false);
        }}
      >
        <MessageSquare size={15} />
        Community
      </button>

      <button
        onClick={() => {
          go("/volunteer");
          setOpenMenu("");
          setMobile(false);
        }}
      >
        <Users size={15} />
        Volunteer
      </button>

      <button
        onClick={() => {
          go("/jobs");
          setOpenMenu("");
          setMobile(false);
        }}
      >
        <BriefcaseBusiness size={15} />
        Jobs
      </button>
    </div>
  </div>

  {/* 6. CONTACT */}
  <button
    className="nav-link"
    onClick={() => {
      go("/contact");
      setOpenMenu("");
      setMobile(false);
    }}
  >
    Contact
  </button>

  {/* MOBILE ADMIN */}
  {(user?.role === "admin" || user?.role === "staff") && (
    <button
      className="nav-link mobile-admin-link"
      onClick={() => {
        go("/admin");
        setOpenMenu("");
        setMobile(false);
      }}
    >
      <ShieldCheck size={16} />
      Admin Panel
    </button>
  )}

</nav>

          <div className="civic-header-actions">

            <button
              className="language-btn"
              onClick={() => setLang(lang === "en" ? "kn" : "en")}
              aria-label={lang === "en" ? "Switch to Kannada" : "Switch to English"}
            >
              <Globe2 size={15} />
              <span>{lang === "en" ? "ಕನ್ನಡ" : "English"}</span>
            </button>

            {user ? (
              <>
                <button
                  className="header-profile"
                  onClick={() => go("/profile")}
                >
                  <UserCircle size={16} />
                  <span>{t.profile}</span>
                </button>

                {(user.role === "admin" || user.role === "staff") && (
                  <button
                    className="header-admin"
                    onClick={() => go("/admin")}
                  >
                    Admin
                  </button>
                )}

                <button
                  className="header-icon"
                  onClick={logout}
                  title={t.logout}
                  aria-label={t.logout}
                >
                  <LogOut size={17} />
                </button>
              </>
            ) : (
              <button className="login-btn" onClick={() => setModal("login")}>
                <LogIn size={16} />
                <span>{t.login}</span>
              </button>
            )}

            <button
              className="mobile-toggle"
              onClick={() => {
                setMobile(!mobile);
                setOpenMenu("");
              }}
              aria-label="Menu"
            >
              {mobile ? <X /> : <Menu />}
            </button>

          </div>
        </div>
      </header>

      <main>
        <section className="civic-hero">
          <div className="hero-inner">
            <div className="hero-content">
              <div className="civic-badge">
                <MapPin size={15} />
                Vijayanagar Civic Connect
              </div>

              <h1>Report local problems. Follow what happens next.</h1>

              <p>
                A civic service platform for residents across the 13 wards of
                Vijayanagar.
              </p>

              <div className="hero-buttons">
                <button
                  className="civic-btn primary large"
                  onClick={() => setModal("report")}
                >
                  <FileText size={18} />
                  {t.reportNow}
                  <ArrowRight size={17} />
                </button>

                <button
                  className="civic-btn secondary large"
                  onClick={() => setModal("track")}
                >
                  <Search size={18} />
                  {t.trackNow}
                </button>
              </div>

              <div className="hero-links">
                <button onClick={() => go("/wards")}>
                  {t.exploreWard} <ArrowRight size={15} />
                </button>

                <button onClick={() => go("/dashboard")}>
                  {t.dashboardBtn} <ArrowRight size={15} />
                </button>
              </div>
            </div>

            <div className="hero-panel">
              <div className="hero-panel-top">
                <span>13</span>
                <div>
                  <strong>Wards</strong>
                  <small>Vijayanagar Assembly</small>
                </div>
              </div>

              <div className="hero-mini-grid">
                <MiniAction icon={<FileText />} title={t.report} onClick={() => setModal("report")} />
                <MiniAction icon={<Search />} title={t.track} onClick={() => setModal("track")} />
                <MiniAction icon={<MapPin />} title={t.wards} onClick={() => go("/wards")} />
                <MiniAction icon={<ShieldCheck />} title={t.dashboard} onClick={() => go("/dashboard")} />
              </div>
            </div>
          </div>
        </section>

        <section className="stats-section">
          <div className="content-width">
            <div className="stats-grid">
              <CivicStat icon={<FileText />} value={dashboard?.stats?.reported ?? 0} label={t.reported} />
              <CivicStat icon={<Clock3 />} value={dashboard?.stats?.in_progress ?? 0} label={t.progress} />
              <CivicStat icon={<CheckCircle2 />} value={dashboard?.stats?.resolved ?? 0} label={t.resolved} />
              <CivicStat icon={<Building2 />} value={dashboard?.stats?.services ?? 24} label={t.servicesCount} />
            </div>
          </div>
        </section>

        <section className="civic-intro-section">
          <div className="content-width civic-intro-grid">
            <div className="civic-intro-copy">
              <span className="eyebrow">A DIGITAL CIVIC SERVICE</span>
              <h2>One place to report, track and follow civic issues.</h2>
              <p>
                Vijayanagar.org is designed to make it easier for residents
                across the Vijayanagar Assembly area to raise civic issues,
                provide useful details, follow complaint progress and access
                ward-level public information.
              </p>

              <div className="civic-intro-points">
                <div>
                  <CheckCircle2 size={18} />
                  <span>Submit complaints with location and photo evidence.</span>
                </div>
                <div>
                  <CheckCircle2 size={18} />
                  <span>Track complaints using a unique tracking ID.</span>
                </div>
                <div>
                  <CheckCircle2 size={18} />
                  <span>View public civic activity and ward information.</span>
                </div>
              </div>
            </div>

            <div className="civic-live-card">
              <span className="eyebrow">LIVE CIVIC ACTIVITY</span>
              <h3>Public activity at a glance</h3>
              <p>
                The figures below are connected to the civic service database
                and update as residents submit and staff process complaints.
              </p>

              <div className="live-mini-stats">
                <div>
                  <strong>{dashboard?.stats?.reported ?? 0}</strong>
                  <span>Reported</span>
                </div>
                <div>
                  <strong>{dashboard?.stats?.in_progress ?? 0}</strong>
                  <span>In Progress</span>
                </div>
                <div>
                  <strong>{dashboard?.stats?.resolved ?? 0}</strong>
                  <span>Resolved</span>
                </div>
              </div>

              <button className="text-link" onClick={() => go("/dashboard")}>
                View public dashboard <ArrowRight size={15} />
              </button>
            </div>
          </div>
        </section>

        <section className="how-section">
          <div className="content-width">
            <SectionHeading
              title="How Vijayanagar Civic Connect works"
              text="A simple digital process for residents to raise an issue and follow what happens next."
            />

            <div className="steps-grid">
              <article className="step-card">
                <div className="step-top">
                  <span>01</span>
                  <FileText size={23} />
                </div>
                <h3>Report an issue</h3>
                <p>
                  Choose the category and sub-category, select your ward,
                  describe the problem and optionally add location and photos.
                </p>
              </article>

              <article className="step-card">
                <div className="step-top">
                  <span>02</span>
                  <Search size={23} />
                </div>
                <h3>Track your complaint</h3>
                <p>
                  Every submitted complaint receives a tracking ID that can
                  be used to check its current status and timeline.
                </p>
              </article>

              <article className="step-card">
                <div className="step-top">
                  <span>03</span>
                  <CheckCircle2 size={23} />
                </div>
                <h3>Follow the progress</h3>
                <p>
                  Residents can see status updates and provide feedback after
                  an issue is marked resolved.
                </p>
              </article>
            </div>
          </div>
        </section>

        <section className="activity-section">
          <div className="content-width activity-grid">
            <div>
              <SectionHeading
                title="Recent civic activity"
                text="Publicly visible complaint activity from the civic service platform."
              />

              <div className="activity-list">
                {(dashboard?.complaints || []).slice(0, 6).map((item) => (
                  <div className="activity-item" key={item.tracking_id}>
                    <div className="activity-icon">
                      <FileText size={17} />
                    </div>
                    <div>
                      <strong>{item.title}</strong>
                      <span>
                        {item.category} · {item.ward}
                      </span>
                    </div>
                    <b>{item.status}</b>
                  </div>
                ))}

                {(!dashboard?.complaints || dashboard.complaints.length === 0) && (
                  <div className="activity-empty">
                    <FileText size={22} />
                    <strong>No public complaints yet</strong>
                    <span>
                      New civic complaints will appear here as they are submitted.
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="activity-events">
              <span className="eyebrow">COMMUNITY ACTIVITY</span>
              <h3>Local civic information</h3>

              {(dashboard?.events || []).slice(0, 5).map((event, index) => (
                <div className="event-item" key={`${event.title}-${index}`}>
                  <div className="event-date">{event.date}</div>
                  <div>
                    <strong>{event.title}</strong>
                    <span>{event.place}</span>
                  </div>
                </div>
              ))}

              {(!dashboard?.events || dashboard.events.length === 0) && (
                <p className="activity-muted">
                  Civic events and public information will appear here.
                </p>
              )}
            </div>
          </div>
        </section>

        <section className="initiative-section">
          <div className="content-width initiative-grid">
            <div className="initiative-image-wrap">
              <img
                src="/images/author-portrait.webp"
                alt="SM Shailesh"
                loading="lazy"
              />
            </div>

            <div className="initiative-copy">
              <span className="eyebrow">ABOUT THIS INITIATIVE</span>
              <h2>Vijayanagar.org is carried out by SM Shailesh.</h2>
              <p>
                Vijayanagar.org is a self-initiated civic service platform
                carried out by <strong>SM Shailesh</strong> to provide residents
                with a convenient digital way to report civic issues, follow
                complaint progress and access public ward information.
              </p>
              <p>
                SM Shailesh has been involved in community and social-service
                activities, including helping residents access medicines,
                medical guidance, health assistance, government welfare
                schemes and subsidised banking support.
              </p>

              <div className="initiative-note">
                <ShieldCheck size={19} />
                <span>
                  The portal is intended to provide civic information and
                  complaint-management services for residents.
                </span>
              </div>

              <button
                className="civic-btn primary"
                onClick={() => go("/author")}
              >
                About SM Shailesh
                <ArrowRight size={17} />
              </button>
            </div>
          </div>
        </section>

        <section className="wards-section">
          <div className="content-width">
            <SectionHeading
              title="13 Wards · One Civic Complaint Platform"
              text="Explore local issues, ward information and civic activity."
            />

            <div className="ward-grid">
              {VIJAYANAGAR_WARDS.map((ward) => (
                <button
                  key={ward.number}
                  className="ward-card"
                  onClick={() => go(wardPath(ward))}
                >
                  <span className="ward-number">Ward {ward.number}</span>
                  <strong>{ward.name}</strong>
                  <ArrowRight size={17} />
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="categories-section">
          <div className="content-width">
            <SectionHeading
              title="What can you report?"
              text="Roads, water, waste, lighting, drainage, parks, schools, health, traffic and more."
            />

            <div className="category-grid">
              {(lang === "en" ? CIVIC_CATEGORIES : CIVIC_CATEGORIES_KN).map(
                (category, index) => (
                  <button
                    className="category-card"
                    key={category}
                    onClick={() => setModal("report")}
                  >
                    <span>{String(index + 1).padStart(2, "0")}</span>
                    <strong>{category}</strong>
                    <ArrowRight size={16} />
                  </button>
                )
              )}
            </div>
          </div>
        </section>

        <section className="public-dashboard-section">
          <div className="content-width dashboard-callout">
            <div>
              <span className="eyebrow">PUBLIC CIVIC INFORMATION</span>
              <h2>Civic Dashboard</h2>
              <p>
                See ward activity, complaint status and public civic information.
              </p>
            </div>

            <button className="civic-btn primary large" onClick={() => go("/dashboard")}>
              {t.dashboardBtn}
              <ArrowRight size={17} />
            </button>
          </div>
        </section>

        <section className="service-community-section">
          <div className="content-width two-column-cards">
            <InfoCard
              icon={<Building2 />}
              title="Civic Services"
              text="Find useful local services and public information."
              button={t.services}
              onClick={() => go("/services")}
            />

            <InfoCard
              icon={<MessageSquare />}
              title="Community Suggestions"
              text="Share a civic suggestion after signing in."
              button={t.community}
              onClick={() => go("/community")}
            />
          </div>
        </section>
      </main>

      <footer className="civic-footer">
        <div className="content-width footer-inner">
          <div>
            <div className="civic-brand footer-brand">
              <span className="civic-logo">V</span>
              <span>vijayanagar<span>.org</span></span>
            </div>
            <p>Vijayanagar Civic Connect</p>
          </div>

          <div className="footer-links">
            <button onClick={() => setModal("report")}>{t.report}</button>
            <button onClick={() => setModal("track")}>{t.track}</button>
            <button onClick={() => go("/wards")}>{t.wards}</button>
            <button onClick={() => go("/dashboard")}>{t.dashboard}</button>
            <button onClick={() => go("/services")}>{t.services}</button>
          </div>
        </div>
      </footer>

      {modal && (
        <Modal
          type={modal}
          user={user}
          setUser={setUser}
          close={() => setModal(null)}
          t={t}
          lang={lang}
        />
      )}
    </div>
  );
}

function Gate({ t, onLogin, onBack }) {
  return (
    <div className="civic-gate">
      <LogIn size={42} />
      <h1>{t.login}</h1>
      <button className="civic-btn primary" onClick={onLogin}>
        {t.login}
      </button>
      <button className="civic-btn secondary" onClick={onBack}>
        {t.home}
      </button>
    </div>
  );
}

function SectionHeading({ title, text }) {
  return (
    <div className="section-heading">
      <span className="eyebrow">CIVIC SERVICES</span>
      <h2>{title}</h2>
      <p>{text}</p>
    </div>
  );
}

function CivicStat({ icon, value, label }) {
  return (
    <div className="civic-stat">
      <div className="stat-icon">{icon}</div>
      <strong>{value}</strong>
      <span>{label}</span>
    </div>
  );
}

function MiniAction({ icon, title, onClick }) {
  return (
    <button className="mini-action" onClick={onClick}>
      {icon}
      <span>{title}</span>
      <ArrowRight size={15} />
    </button>
  );
}

function InfoCard({ icon, title, text, button, onClick }) {
  return (
    <article className="info-card">
      <div className="info-card-icon">{icon}</div>
      <h3>{title}</h3>
      <p>{text}</p>
      <button onClick={onClick}>
        {button}
        <ArrowRight size={16} />
      </button>
    </article>
  );
}

function ContactPage({ onBack }) {
  return (
    <div className="civic-site">
      <main className="contact-page">
        <section className="contact-hero">
          <div className="contact-hero-inner">
            <button
              className="simple-back"
              onClick={onBack}
            >
              <ArrowLeft size={17} />
              Back
            </button>

            <span className="eyebrow">CONTACT VIJAYANAGAR CIVIC PORTAL</span>

            <h1>Contact Us</h1>

            <p>
              Have a civic issue, question or suggestion about the portal?
              Get in touch with us and we will help you find the appropriate
              civic service or information.
            </p>
          </div>
        </section>

        <section className="contact-content">
          <div className="contact-grid">

            <article className="contact-card">
              <div className="contact-icon">
                <MessageSquare size={22} />
              </div>

              <div>
                <span className="contact-label">Civic Assistance</span>
                <h2>Need help with the portal?</h2>
                <p>
                  For assistance with reporting an issue, tracking a complaint,
                  accessing your reports or using civic services, please use
                  the relevant option in the portal.
                </p>
              </div>

              <div className="contact-actions">
                <button
                  className="contact-button primary"
                  onClick={() => window.history.back()}
                >
                  <FileText size={16} />
                  Report an Issue
                </button>
              </div>
            </article>

            <article className="contact-card">
              <div className="contact-icon">
                <MapPin size={22} />
              </div>

              <div>
                <span className="contact-label">Vijayanagar</span>
                <h2>Local Civic Information</h2>
                <p>
                  Explore ward information, civic services, community
                  resources and publicly available civic activity through
                  the portal.
                </p>
              </div>

              <div className="contact-actions">
                <button
                  className="contact-button"
                  onClick={() => {
                    window.location.href = "/wards";
                  }}
                >
                  <MapPin size={16} />
                  Explore Wards
                </button>
              </div>
            </article>

          </div>

          <div className="contact-note">
            <ShieldCheck size={19} />
            <div>
              <strong>For complaint-related matters</strong>
              <p>
                Please submit the issue through the Report Issue section so
                that a tracking ID can be generated and the complaint can be
                followed through the portal.
              </p>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}


function VolunteerPage({ onBack, go }) {
  const [submitted, setSubmitted] = useState(false);

  const submitVolunteer = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <div className="volunteer-page">

      <section className="volunteer-hero">
        <div className="volunteer-hero-inner">

          <div className="volunteer-hero-copy">
            <span className="volunteer-kicker">
              CIVIC LEADERSHIP • COMMUNITY SERVICE
            </span>

            <h1>
              Young people can lead by
              <span> serving their community.</span>
            </h1>

            <p className="volunteer-hero-text">
              Leadership begins with listening, taking responsibility and
              helping people solve everyday problems. Join a civic volunteer
              initiative where young residents can learn, contribute and
              create meaningful change in their communities.
            </p>

            <div className="volunteer-hero-actions">
              <a href="#volunteer-join" className="volunteer-primary-btn">
                Become a Volunteer
              </a>

              <a href="#volunteer-opportunities" className="volunteer-secondary-btn">
                Explore Opportunities
              </a>
            </div>

            <div className="volunteer-trust-line">
              <ShieldCheck size={18} />
              <span>
                Open to residents interested in community service and civic
                participation.
              </span>
            </div>
          </div>

          <div className="volunteer-hero-photo-wrap">
            <div className="volunteer-photo-glow"></div>

            <div className="volunteer-photo-card">
              <img
                src="/images/author-portrait.webp"
                alt="SM Shailesh"
                className="volunteer-hero-photo"
              />

              <div className="volunteer-photo-caption">
                <strong>SM Shailesh</strong>
                <span>Civic initiative coordinator</span>
              </div>
            </div>
          </div>

        </div>
      </section>


      <section className="volunteer-intro">
        <div className="volunteer-section-heading">
          <span className="volunteer-kicker">WHY VOLUNTEER?</span>
          <h2>Leadership is built through service.</h2>
          <p>
            A strong community needs people who are willing to listen,
            understand local concerns and work constructively with others.
            Volunteering is an opportunity to develop those skills through
            real community experience.
          </p>
        </div>

        <div className="volunteer-values-grid">

          <div className="volunteer-value-card">
            <div className="volunteer-value-icon">
              <Users size={22} />
            </div>
            <h3>Listen to People</h3>
            <p>
              Understand the concerns, needs and experiences of residents
              before looking for solutions.
            </p>
          </div>

          <div className="volunteer-value-card">
            <div className="volunteer-value-icon">
              <HeartHandshake size={22} />
            </div>
            <h3>Serve with Empathy</h3>
            <p>
              Help people navigate civic services and connect them with
              appropriate information and resources.
            </p>
          </div>

          <div className="volunteer-value-card">
            <div className="volunteer-value-icon">
              <Lightbulb size={22} />
            </div>
            <h3>Learn Leadership</h3>
            <p>
              Build communication, teamwork, organisation and problem-solving
              skills through practical community work.
            </p>
          </div>

          <div className="volunteer-value-card">
            <div className="volunteer-value-icon">
              <MapPin size={22} />
            </div>
            <h3>Improve Your Community</h3>
            <p>
              Help identify local civic concerns and support constructive
              efforts to improve neighbourhoods.
            </p>
          </div>

        </div>
      </section>


      <section className="volunteer-message">
        <div className="volunteer-message-inner">

          <div className="volunteer-message-mark">
            “
          </div>

          <div>
            <span className="volunteer-kicker">A MESSAGE FOR YOUNG VOLUNTEERS</span>

            <h2>
              Don't wait for a title to start making a difference.
            </h2>

            <p>
              Real leadership starts with small acts of responsibility:
              helping a neighbour, listening to a concern, understanding how
              a civic service works, bringing people together and following
              through on a promise.
            </p>

            <p>
              If you are young and want to develop your leadership abilities,
              community volunteering can give you the experience, discipline
              and perspective that no classroom can provide on its own.
            </p>

            <div className="volunteer-message-sign">
              <strong>SM Shailesh</strong>
              <span>Community & civic initiative</span>
            </div>
          </div>

        </div>
      </section>


      <section id="volunteer-opportunities" className="volunteer-opportunities">
        <div className="volunteer-section-heading">
          <span className="volunteer-kicker">HOW YOU CAN HELP</span>
          <h2>Choose an area where you can contribute.</h2>
          <p>
            Volunteers can participate according to their interests, skills
            and available time.
          </p>
        </div>

        <div className="volunteer-opportunity-grid">

          <div className="volunteer-opportunity">
            <Users size={22} />
            <h3>Community Outreach</h3>
            <p>
              Speak with residents, listen to local concerns and help share
              information about civic services.
            </p>
          </div>

          <div className="volunteer-opportunity">
            <FileText size={22} />
            <h3>Civic Awareness</h3>
            <p>
              Help residents understand how to report civic problems and
              follow the progress of their complaints.
            </p>
          </div>

          <div className="volunteer-opportunity">
            <MapPin size={22} />
            <h3>Neighbourhood Observation</h3>
            <p>
              Identify visible civic concerns such as damaged roads,
              drainage issues, waste problems and street-light concerns.
            </p>
          </div>

          <div className="volunteer-opportunity">
            <MessageSquare size={22} />
            <h3>Communication</h3>
            <p>
              Help collect feedback and communicate community information
              clearly and respectfully.
            </p>
          </div>

          <div className="volunteer-opportunity">
            <Lightbulb size={22} />
            <h3>Youth Initiatives</h3>
            <p>
              Bring ideas for constructive community activities, awareness
              drives and neighbourhood improvement.
            </p>
          </div>

          <div className="volunteer-opportunity">
            <HeartHandshake size={22} />
            <h3>Community Service</h3>
            <p>
              Participate in activities that support residents and promote
              cleaner, safer and better-connected neighbourhoods.
            </p>
          </div>

        </div>
      </section>


      <section className="volunteer-steps">
        <div className="volunteer-section-heading">
          <span className="volunteer-kicker">GET STARTED</span>
          <h2>Your journey can start with one simple step.</h2>
        </div>

        <div className="volunteer-step-grid">

          <div className="volunteer-step">
            <span>01</span>
            <h3>Introduce Yourself</h3>
            <p>
              Tell us a little about yourself, your interests and the area
              where you would like to contribute.
            </p>
          </div>

          <div className="volunteer-step">
            <span>02</span>
            <h3>Choose Your Interest</h3>
            <p>
              Select the type of community activity that matches your skills
              and availability.
            </p>
          </div>

          <div className="volunteer-step">
            <span>03</span>
            <h3>Start Serving</h3>
            <p>
              Work respectfully with residents and fellow volunteers while
              learning from practical community experience.
            </p>
          </div>

        </div>
      </section>


      <section id="volunteer-join" className="volunteer-join">
        <div className="volunteer-join-card">

          <div className="volunteer-join-copy">
            <span className="volunteer-kicker">JOIN THE CIVIC VOLUNTEER NETWORK</span>
            <h2>Ready to contribute?</h2>
            <p>
              Share your details and interests. A volunteer coordinator can
              contact you with information about suitable community activities.
            </p>

            <div className="volunteer-join-points">
              <span><CheckCircle2 size={18} /> Community-focused</span>
              <span><CheckCircle2 size={18} /> Skills & leadership development</span>
              <span><CheckCircle2 size={18} /> Respectful and inclusive participation</span>
            </div>
          </div>

          <form className="volunteer-form" onSubmit={submitVolunteer}>

            <label>
              Full Name
              <input required name="name" placeholder="Your name" />
            </label>

            <label>
              Mobile Number
              <input required name="phone" type="tel" placeholder="10-digit mobile number" />
            </label>

            <label>
              Area / Ward
              <input name="area" placeholder="Your locality or ward" />
            </label>

            <label>
              Area of Interest
              <select name="interest" defaultValue="">
                <option value="" disabled>Select an area</option>
                <option>Community Outreach</option>
                <option>Civic Awareness</option>
                <option>Youth Initiatives</option>
                <option>Neighbourhood Observation</option>
                <option>Communication</option>
                <option>Community Service</option>
              </select>
            </label>

            <label>
              Tell us about yourself
              <textarea
                name="message"
                rows="4"
                placeholder="Your skills, interests or how you would like to contribute"
              ></textarea>
            </label>

            <button type="submit" className="volunteer-submit-btn">
              <Users size={18} />
              Join as a Volunteer
            </button>

            {submitted && (
              <div className="volunteer-form-success">
                <CheckCircle2 size={20} />
                <div>
                  <strong>Thank you for your interest.</strong>
                  <span>Your volunteer request has been noted.</span>
                </div>
              </div>
            )}

          </form>

        </div>
      </section>


      <section className="volunteer-bottom">
        <h2>Serve. Learn. Lead.</h2>
        <p>
          Start by helping your community today. Leadership grows from
          responsibility, consistency and service.
        </p>

        <div className="volunteer-bottom-actions">
          <a href="#volunteer-join" className="volunteer-primary-btn">
            Become a Volunteer
          </a>

          <button
            type="button"
            className="volunteer-secondary-btn volunteer-back-btn"
            onClick={onBack}
          >
            Back to Home
          </button>
        </div>
      </section>

    </div>
  );
}



function WardsPage({ t, onBack, onWard }) {
  return (
    <SimplePage title="Vijayanagar · 13 Wards" onBack={onBack}>
      <div className="ward-grid">
        {VIJAYANAGAR_WARDS.map((ward) => (
          <button className="ward-card" key={ward.number} onClick={() => onWard(ward)}>
            <span className="ward-number">Ward {ward.number}</span>
            <strong>{ward.name}</strong>
            <ArrowRight size={17} />
          </button>
        ))}
      </div>
    </SimplePage>
  );
}

function WardPage({ ward, t, onBack, user, setUser }) {
  const [data, setData] = useState(null);
  const [wardModal, setWardModal] = useState(null);

  useEffect(() => {
    api.get(`/wards/${ward.number}`).then((r) => setData(r.data)).catch(() => {});
  }, [ward.number]);

  return (
    <>
      <SimplePage title={`Ward ${ward.number} · ${ward.name}`} onBack={onBack}>
        <div className="ward-stat-row">
          <CivicStat icon={<FileText />} value={data?.stats?.total ?? 0} label={t.reported} />
          <CivicStat icon={<Clock3 />} value={data?.stats?.in_progress ?? 0} label={t.progress} />
          <CivicStat icon={<CheckCircle2 />} value={data?.stats?.resolved ?? 0} label={t.resolved} />
        </div>

        <div className="ward-content-grid">
          <div className="ward-info-panel">
            <span className="eyebrow">WARD {ward.number}</span>
            <h2>Ward Civic Activity</h2>
            <p>
              View current complaint activity and category distribution for this ward.
            </p>

            <div className="hero-buttons">
              <button
                type="button"
                className="civic-btn primary"
                onClick={() => setWardModal("report")}
              >
                <FileText size={17} />
                {t.report}
              </button>

              <button
                type="button"
                className="civic-btn secondary"
                onClick={() => setWardModal("track")}
              >
                <Search size={17} />
                {t.track}
              </button>
            </div>
          </div>

          <div className="ward-map-card">
            <MapPin size={25} />
            <strong>{ward.name}</strong>
            <span>Ward {ward.number}</span>
          </div>
        </div>

        <section className="civic-data-panel">
          <h2>Issues by Category</h2>
          {(data?.categories || []).length === 0 ? (
            <p>No complaints reported in this ward yet.</p>
          ) : (
            data.categories.map((item) => (
              <div className="data-row" key={item.category}>
                <span>{item.category}</span>
                <strong>{item.count}</strong>
              </div>
            ))
          )}
        </section>

        <section className="civic-data-panel">
          <h2>Recent Complaints</h2>
          {(data?.recent_complaints || []).map((item) => (
            <div className="data-row" key={item.tracking_id}>
              <span>
                <strong>{item.tracking_id}</strong> · {item.title}
              </span>
              <b>{item.status}</b>
            </div>
          ))}
        </section>
      </SimplePage>

      {wardModal && (
        <Modal
          type={wardModal}
          user={user}
          setUser={setUser}
          close={() => setWardModal(null)}
          t={t}
        />
      )}
    </>
  );
}

function DashboardPage({ t, onBack }) {
  const [dashboard, setDashboard] = useState(null);
  const [mapData, setMapData] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/dashboard"),
      api.get("/dashboard/complaints-map"),
    ])
      .then(([d, m]) => {
        setDashboard(d.data);
        setMapData(m.data.complaints || []);
      })
      .catch(() => {});
  }, []);

  return (
    <SimplePage title="Public Civic Dashboard" onBack={onBack}>
      <div className="stats-grid">
        <CivicStat icon={<FileText />} value={dashboard?.stats?.reported ?? 0} label={t.reported} />
        <CivicStat icon={<Clock3 />} value={dashboard?.stats?.in_progress ?? 0} label={t.progress} />
        <CivicStat icon={<CheckCircle2 />} value={dashboard?.stats?.resolved ?? 0} label={t.resolved} />
        <CivicStat icon={<Building2 />} value={dashboard?.stats?.services ?? 0} label={t.servicesCount} />
      </div>

      <div className="civic-data-panel">
        <h2>Issue Map</h2>
        <LeafletMap complaints={mapData} />
      </div>

      <div className="civic-data-panel">
        <h2>Recent Public Issues</h2>
        {(dashboard?.complaints || []).slice(0, 30).map((item) => (
          <div className="data-row" key={item.tracking_id}>
            <span>
              <strong>{item.tracking_id}</strong> · {item.title}
            </span>
            <b>{item.status}</b>
          </div>
        ))}
      </div>
    </SimplePage>
  );
}


function JobsBoard({ onBack, onPostJob }) {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("All");
  const [type, setType] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/jobs")
      .then((r) => setItems(r.data.items || r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const categories = [
    "All",
    ...Array.from(
      new Set(items.map((x) => x.category).filter(Boolean))
    ),
  ];

  const filtered = items.filter((job) => {
    const q = search.trim().toLowerCase();

    const matchesSearch =
      !q ||
      [
        job.title,
        job.org,
        job.location,
        job.category,
        job.description,
        job.skills,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q);

    const matchesCategory =
      category === "All" || job.category === category;

    const matchesType =
      type === "All" || job.employment_type === type;

    return matchesSearch && matchesCategory && matchesType;
  });

  const whatsappNumber = (value) =>
    String(value || "").replace(/\D/g, "");

  return (
    <SimplePage title="Local Jobs & Opportunities" onBack={onBack}>
      <div className="jobs-hero-panel">
        <div>
          <span className="jobs-kicker">VIJAYANAGAR JOB CONNECT</span>
          <h2>Find local jobs. Contact employers directly.</h2>
          <p>
            Discover employment opportunities posted for candidates in and
            around Vijayanagar. Contact employers directly by phone,
            WhatsApp, email or their application link.
          </p>
        </div>

        <button className="civic-btn primary" onClick={onPostJob}>
          Post a Job
        </button>
      </div>

      <div className="jobs-disclaimer">
        <strong>For employers:</strong> Job listings are reviewed before
        publication. The portal facilitates listings and direct contact;
        candidates should independently verify employers, job details and
        any requests for money or documents.
      </div>

      <div className="jobs-filters">
        <div className="jobs-search">
          <Search size={18} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search jobs, skills, companies..."
          />
        </div>

        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
        >
          {categories.map((x) => (
            <option key={x}>{x}</option>
          ))}
        </select>

        <select
          value={type}
          onChange={(e) => setType(e.target.value)}
        >
          <option>All</option>
          <option>Full-time</option>
          <option>Part-time</option>
          <option>Contract</option>
          <option>Internship</option>
          <option>Temporary</option>
        </select>
      </div>

      {loading ? (
        <div className="jobs-empty">Loading jobs...</div>
      ) : filtered.length === 0 ? (
        <div className="jobs-empty">
          <BriefcaseBusiness size={34} />
          <h3>No matching jobs found</h3>
          <p>Try another search or category.</p>
        </div>
      ) : (
        <div className="jobs-grid">
          {filtered.map((job, i) => {
            const phone = whatsappNumber(job.employer_phone);

            return (
              <article className="job-card-pro" key={job.id || i}>
                <div className="job-card-top">
                  <div className="job-icon">
                    <BriefcaseBusiness size={22} />
                  </div>

                  <div>
                    <h3>{job.title}</h3>
                    <p className="job-company">
                      {job.org || "Employer"}
                    </p>
                  </div>
                </div>

                <div className="job-meta-grid">
                  {job.location && (
                    <span>
                      <MapPin size={15} />
                      {job.location}
                    </span>
                  )}

                  {job.employment_type && (
                    <span>
                      <BriefcaseBusiness size={15} />
                      {job.employment_type}
                    </span>
                  )}

                  {job.salary && (
                    <span>
                      ₹ {job.salary}
                    </span>
                  )}

                  {job.experience && (
                    <span>
                      {job.experience}
                    </span>
                  )}
                </div>

                {job.category && (
                  <span className="job-category">
                    {job.category}
                  </span>
                )}

                <p className="job-description">
                  {job.description || "Job opportunity available."}
                </p>

                <div className="job-extra">
                  {job.qualification && (
                    <div>
                      <strong>Qualification:</strong>{" "}
                      {job.qualification}
                    </div>
                  )}

                  {job.openings && (
                    <div>
                      <strong>Openings:</strong>{" "}
                      {job.openings}
                    </div>
                  )}

                  {job.deadline && (
                    <div>
                      <strong>Apply before:</strong>{" "}
                      {job.deadline}
                    </div>
                  )}
                </div>

                <div className="job-actions">
                  {job.employer_phone && (
                    <a
                      className="job-action phone"
                      href={`tel:${job.employer_phone}`}
                    >
                      <Phone size={16} />
                      Call
                    </a>
                  )}

                  {phone && (
                    <a
                      className="job-action whatsapp"
                      href={`https://wa.me/${phone}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <MessageCircle size={16} />
                      WhatsApp
                    </a>
                  )}

                  {job.employer_email && (
                    <a
                      className="job-action email"
                      href={`mailto:${job.employer_email}`}
                    >
                      <Mail size={16} />
                      Email
                    </a>
                  )}

                  {job.apply_link && (
                    <a
                      className="job-action apply"
                      href={job.apply_link}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Apply Online
                    </a>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </SimplePage>
  );
}


function EmployerJobPage({ onBack, onJobs }) {
  const [form, setForm] = useState({
    title: "",
    org: "",
    description: "",
    location: "",
    apply_link: "",
    employer_name: "",
    employer_phone: "",
    employer_email: "",
    category: "",
    employment_type: "Full-time",
    salary: "",
    experience: "",
    qualification: "",
    openings: "1",
    deadline: "",
    contact_preference: "Phone",
    website: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const change = (key, value) => {
    setForm((old) => ({ ...old, [key]: value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSuccess("");
    setError("");

    try {
      const r = await api.post("/jobs", form);

      setSuccess(
        r.data?.message ||
        "Job submitted successfully for review."
      );

      setForm({
        title: "",
        org: "",
        description: "",
        location: "",
        apply_link: "",
        employer_name: "",
        employer_phone: "",
        employer_email: "",
        category: "",
        employment_type: "Full-time",
        salary: "",
        experience: "",
        qualification: "",
        openings: "1",
        deadline: "",
        contact_preference: "Phone",
        website: "",
      });
    } catch (err) {
      setError(
        err.response?.data?.detail ||
        "Unable to submit the job. Please check the information and try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SimplePage title="Post a Job" onBack={onBack}>
      <div className="employer-page-intro">
        <span className="jobs-kicker">EMPLOYER JOB POSTING</span>
        <h2>Connect with candidates in your local community.</h2>
        <p>
          Submit your vacancy with complete employer contact information.
          After review and approval, candidates will be able to contact
          you directly.
        </p>
      </div>

      {success && (
        <div className="job-success">
          <CheckCircle2 size={20} />
          <div>
            <strong>Job submitted successfully</strong>
            <p>{success}</p>
          </div>
        </div>
      )}

      {error && (
        <div className="job-error">
          {error}
        </div>
      )}

      <form className="employer-form" onSubmit={submit}>
        <div className="employer-form-section">
          <h3>Job Information</h3>

          <div className="employer-form-grid">
            <label>
              Job Title *
              <input
                value={form.title}
                onChange={(e) => change("title", e.target.value)}
                placeholder="e.g. Sales Executive"
                required
              />
            </label>

            <label>
              Company / Organisation *
              <input
                value={form.org}
                onChange={(e) => change("org", e.target.value)}
                placeholder="Company name"
                required
              />
            </label>

            <label>
              Job Category
              <input
                value={form.category}
                onChange={(e) => change("category", e.target.value)}
                placeholder="Sales, IT, Retail, Hospitality..."
              />
            </label>

            <label>
              Employment Type
              <select
                value={form.employment_type}
                onChange={(e) => change("employment_type", e.target.value)}
              >
                <option>Full-time</option>
                <option>Part-time</option>
                <option>Contract</option>
                <option>Internship</option>
                <option>Temporary</option>
              </select>
            </label>

            <label>
              Work Location *
              <input
                value={form.location}
                onChange={(e) => change("location", e.target.value)}
                placeholder="Vijayanagar, Bengaluru"
                required
              />
            </label>

            <label>
              Salary / Salary Range
              <input
                value={form.salary}
                onChange={(e) => change("salary", e.target.value)}
                placeholder="e.g. ₹18,000 – ₹25,000/month"
              />
            </label>

            <label>
              Experience
              <input
                value={form.experience}
                onChange={(e) => change("experience", e.target.value)}
                placeholder="e.g. 0–2 years"
              />
            </label>

            <label>
              Qualification
              <input
                value={form.qualification}
                onChange={(e) => change("qualification", e.target.value)}
                placeholder="e.g. 12th / Graduate"
              />
            </label>

            <label>
              Number of Openings
              <input
                value={form.openings}
                onChange={(e) => change("openings", e.target.value)}
                placeholder="1"
              />
            </label>

            <label>
              Application Deadline
              <input
                value={form.deadline}
                onChange={(e) => change("deadline", e.target.value)}
                placeholder="e.g. 15 October 2026"
              />
            </label>
          </div>
        </div>

        <div className="employer-form-section">
          <h3>Job Description</h3>

          <label>
            Describe the role, responsibilities and requirements *
            <textarea
              rows="8"
              value={form.description}
              onChange={(e) => change("description", e.target.value)}
              placeholder="Describe the job, responsibilities, required skills and other relevant information..."
              required
            />
          </label>
        </div>

        <div className="employer-form-section">
          <h3>Employer Contact</h3>

          <div className="employer-form-grid">
            <label>
              Recruiter / Contact Person *
              <input
                value={form.employer_name}
                onChange={(e) => change("employer_name", e.target.value)}
                placeholder="Contact person name"
                required
              />
            </label>

            <label>
              Phone Number *
              <input
                value={form.employer_phone}
                onChange={(e) => change("employer_phone", e.target.value)}
                placeholder="Employer contact number"
                required
              />
            </label>

            <label>
              Email Address
              <input
                type="email"
                value={form.employer_email}
                onChange={(e) => change("employer_email", e.target.value)}
                placeholder="recruiter@company.com"
              />
            </label>

            <label>
              Preferred Contact
              <select
                value={form.contact_preference}
                onChange={(e) => change("contact_preference", e.target.value)}
              >
                <option>Phone</option>
                <option>WhatsApp</option>
                <option>Email</option>
                <option>Website</option>
              </select>
            </label>

            <label>
              Company Website
              <input
                value={form.website}
                onChange={(e) => change("website", e.target.value)}
                placeholder="https://example.com"
              />
            </label>

            <label>
              Apply Online URL
              <input
                value={form.apply_link}
                onChange={(e) => change("apply_link", e.target.value)}
                placeholder="https://example.com/careers"
              />
            </label>
          </div>
        </div>

        <div className="jobs-posting-notice">
          <strong>Before submitting</strong>
          <p>
            Please provide accurate employment information. Job listings
            are reviewed before they are published. Candidates should
            independently verify the employer and job opportunity.
          </p>
        </div>

        <div className="employer-form-actions">
          <button
            type="button"
            className="civic-btn secondary"
            onClick={onJobs}
          >
            View Jobs
          </button>

          <button
            type="submit"
            className="civic-btn primary"
            disabled={submitting}
          >
            {submitting ? "Submitting..." : "Submit Job for Review"}
          </button>
        </div>
      </form>
    </SimplePage>
  );
}




function PublicServicesPage({ onBack, go }) {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  const categories = [
    {
      slug: "temples",
      title: "Temples",
      description: "Temples and places of worship in and around Vijayanagar.",
      icon: <Building2 size={28} />,
    },
    {
      slug: "hospitals",
      title: "Hospitals",
      description: "Hospitals and healthcare facilities available to residents.",
      icon: <Building2 size={28} />,
    },
    {
      slug: "police-stations",
      title: "Police Stations",
      description: "Police stations and local public safety information.",
      icon: <ShieldCheck size={28} />,
    },
    {
      slug: "libraries",
      title: "Libraries",
      description: "Libraries and public learning resources in the area.",
      icon: <FileText size={28} />,
    },
    {
      slug: "schools",
      title: "Schools",
      description: "Schools and public education-related information.",
      icon: <Building2 size={28} />,
    },
  ];

  useEffect(() => {
    api.get("/services")
      .then((r) => {
        setServices(r.data.items || r.data || []);
      })
      .catch(() => {
        setServices([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const getCount = (slug) => {
    const keywords = {
      temples: ["temple", "temples", "mandir"],
      hospitals: ["hospital", "hospitals", "healthcare", "medical"],
      "police-stations": ["police", "police station", "police stations"],
      libraries: ["library", "libraries"],
      schools: ["school", "schools", "education"],
    };

    return services.filter((item) => {
      const category = String(item.category || "").toLowerCase().trim();

      if (category === slug) return true;

      const text = [
        item.name,
        item.title,
        item.description,
        item.category,
        item.type,
        item.service_type,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return keywords[slug].some((keyword) => text.includes(keyword));
    }).length;
  };

  return (
    <SimplePage title="Public Services" onBack={onBack}>
      <div className="services-modern">

        <section className="services-modern-hero">
          <div className="services-modern-hero-copy">
            <span className="eyebrow">PUBLIC SERVICES · VIJAYANAGAR</span>

            <h2>
              Find the places and services
              <span> you need nearby.</span>
            </h2>

            <p>
              Browse local public-service information by category. Find
              useful places, contact details, addresses and opening hours
              from one convenient portal.
            </p>
          </div>

          <div className="services-modern-hero-mark">
            <Building2 size={42} />
            <span>5</span>
            <small>service categories</small>
          </div>
        </section>

        <section className="services-category-section">
          <div className="services-section-heading">
            <div>
              <span className="eyebrow">EXPLORE</span>
              <h2>Public Services</h2>
            </div>

            <p>
              Select a category to view available places and information.
            </p>
          </div>

          <div className="services-category-grid">
            {categories.map((category, index) => (
              <button
                type="button"
                key={category.slug}
                className="services-category-card"
                onClick={() => go(`/services/${category.slug}`)}
              >
                <div className="services-category-top">
                  <span className="services-category-number">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <span className="services-category-arrow">
                    <ArrowRight size={18} />
                  </span>
                </div>

                <div className="services-category-icon">
                  {category.icon}
                </div>

                <div className="services-category-body">
                  <h3>{category.title}</h3>
                  <p>{category.description}</p>
                </div>

                <div className="services-category-footer">
                  <span>
                    {loading
                      ? "Loading..."
                      : `${getCount(category.slug)} ${getCount(category.slug) === 1 ? "listing" : "listings"}`}
                  </span>

                  <strong>
                    Explore <ArrowRight size={15} />
                  </strong>
                </div>
              </button>
            ))}
          </div>
        </section>

        <section className="services-info-strip">
          <div className="services-info-icon">
            <ShieldCheck size={20} />
          </div>

          <div>
            <strong>Public information directory</strong>
            <p>
              Information displayed here is provided to help residents locate
              and access public services. Availability and timings may change,
              so residents should verify details where necessary.
            </p>
          </div>
        </section>

      </div>
    </SimplePage>
  );
}


function PublicServiceCategoryPage({ category, onBack }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  const categoryConfig = {
    temples: {
      title: "Temples",
      eyebrow: "PLACES OF WORSHIP",
      description:
        "Temples and places of worship in and around Vijayanagar.",
      keywords: ["temple", "temples", "mandir"],
    },

    hospitals: {
      title: "Hospitals",
      eyebrow: "HEALTHCARE",
      description:
        "Hospitals and healthcare facilities available to residents.",
      keywords: ["hospital", "hospitals", "healthcare", "medical"],
    },

    "police-stations": {
      title: "Police Stations",
      eyebrow: "PUBLIC SAFETY",
      description:
        "Police stations and local public safety information.",
      keywords: ["police", "police station", "police stations"],
    },

    libraries: {
      title: "Libraries",
      eyebrow: "LEARNING & READING",
      description:
        "Libraries and public learning resources in the area.",
      keywords: ["library", "libraries"],
    },

    schools: {
      title: "Schools",
      eyebrow: "EDUCATION",
      description:
        "Schools and public education-related information.",
      keywords: ["school", "schools", "education"],
    },
  };

  const config =
    categoryConfig[category] || categoryConfig.temples;

  useEffect(() => {
    setLoading(true);

    api.get("/services")
      .then((r) => {
        const all = r.data.items || r.data || [];

        const filtered = all.filter((item) => {
          const itemCategory = String(item.category || "")
            .toLowerCase()
            .trim();

          if (itemCategory === category) return true;

          const text = [
            item.name,
            item.title,
            item.description,
            item.category,
            item.type,
            item.service_type,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();

          return config.keywords.some((keyword) =>
            text.includes(keyword)
          );
        });

        setItems(filtered);
      })
      .catch(() => {
        setItems([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [category]);

  const filteredItems = items.filter((item) => {
    const q = query.trim().toLowerCase();

    if (!q) return true;

    return [
      item.name,
      item.title,
      item.address,
      item.phone,
      item.hours,
      item.category,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .includes(q);
  });

  return (
    <SimplePage title={config.title} onBack={onBack}>
      <div className="service-directory-modern">

        <section className="service-directory-hero">
          <div>
            <span className="eyebrow">{config.eyebrow}</span>

            <h2>{config.title}</h2>

            <p>{config.description}</p>
          </div>

          <div className="service-directory-count">
            <strong>{items.length}</strong>
            <span>
              {items.length === 1 ? "listing" : "listings"}
            </span>
          </div>
        </section>

        <div className="service-directory-toolbar">
          <div className="service-search">
            <Search size={18} />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search ${config.title.toLowerCase()}...`}
              aria-label={`Search ${config.title}`}
            />
          </div>

          <span className="service-result-count">
            {loading
              ? "Loading..."
              : `${filteredItems.length} result${filteredItems.length === 1 ? "" : "s"}`}
          </span>
        </div>

        {loading ? (
          <div className="services-loading-grid">
            {[1, 2, 3].map((n) => (
              <div className="service-skeleton" key={n}>
                <div />
                <div />
                <div />
              </div>
            ))}
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="service-empty-state">
            <div className="service-empty-icon">
              <Building2 size={30} />
            </div>

            <h3>
              {query
                ? "No matching listings"
                : `No ${config.title.toLowerCase()} listed yet`}
            </h3>

            <p>
              {query
                ? "Try a different search term."
                : "Public listings for this category will appear here when they are added through the administration dashboard."}
            </p>

            {query && (
              <button
                type="button"
                className="civic-btn secondary"
                onClick={() => setQuery("")}
              >
                Clear Search
              </button>
            )}
          </div>
        ) : (
          <div className="service-list-modern">
            {filteredItems.map((item, index) => {
              const name = item.name || item.title || "Public Service";

              return (
                <article
                  className="service-place-card"
                  key={item.id || `${name}-${index}`}
                >
                  <div className="service-place-icon">
                    {category === "police-stations" ? (
                      <ShieldCheck size={23} />
                    ) : (
                      <Building2 size={23} />
                    )}
                  </div>

                  <div className="service-place-main">
                    <div className="service-place-heading">
                      <div>
                        <span className="service-place-label">
                          {config.title}
                        </span>
                        <h3>{name}</h3>
                      </div>
                    </div>

                    <div className="service-place-details">
                      {item.address && (
                        <div>
                          <MapPin size={16} />
                          <span>{item.address}</span>
                        </div>
                      )}

                      {item.phone && (
                        <div>
                          <Phone size={16} />
                          <span>{item.phone}</span>
                        </div>
                      )}

                      {item.hours && (
                        <div>
                          <Clock3 size={16} />
                          <span>{item.hours}</span>
                        </div>
                      )}
                    </div>

                    <div className="service-place-actions">
                      {item.phone && (
                        <a
                          href={`tel:${String(item.phone).replace(/\s+/g, "")}`}
                          className="service-action"
                        >
                          <Phone size={15} />
                          Call
                        </a>
                      )}

                      {item.address && (
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(item.address)}`}
                          target="_blank"
                          rel="noreferrer"
                          className="service-action secondary"
                        >
                          <MapPin size={15} />
                          Directions
                        </a>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}

      </div>
    </SimplePage>
  );
}



function DirectoryPage({ type, t, onBack }) {
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get(type === "services" ? "/services" : "/jobs")
      .then((r) => setItems(r.data.items || r.data || []))
      .catch(() => {});
  }, [type]);

  return (
    <SimplePage
      title={type === "services" ? "Civic Services" : "Local Jobs"}
      onBack={onBack}
    >
      <div className="directory-grid">
        {items.map((item, i) => (
          <article className="directory-card" key={item.id || i}>
            <Building2 size={24} />
            <h3>{item.name || item.title}</h3>
            <p>{item.description || item.category || ""}</p>
            {item.address && <small>{item.address}</small>}
            {item.location && <small>{item.location}</small>}
            {item.phone && <small>{item.phone}</small>}
            {item.apply_link && (
              <a href={item.apply_link} target="_blank" rel="noreferrer">
                Apply
              </a>
            )}
          </article>
        ))}
      </div>
    </SimplePage>
  );
}

function CommunityPage({ user, t, onBack, onLogin }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();

    if (!user) {
      onLogin();
      return;
    }

    try {
      await api.post("/suggestions", { title, description });
      setTitle("");
      setDescription("");
      setMessage("Suggestion submitted.");
      setError("");
    } catch (err) {
      setError(err.response?.data?.detail || "Unable to submit suggestion.");
    }
  };

  return (
    <SimplePage title="Community Suggestions" onBack={onBack}>
      <div className="civic-data-panel">
        <p>
          Share a constructive civic suggestion for improving local public services.
        </p>

        <form onSubmit={submit} className="profile-grid">
          <label>
            Title
            <input value={title} onChange={(e) => setTitle(e.target.value)} required />
          </label>

          <label className="profile-full">
            Description
            <textarea
              rows="6"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
            />
          </label>

          {message && <div className="profile-success">{message}</div>}
          {error && <div className="profile-error">{error}</div>}

          <button className="civic-btn primary" type="submit">
            Submit Suggestion
          </button>
        </form>
      </div>
    </SimplePage>
  );
}

function SimplePage({ title, onBack, children }) {
  return (
    <div className="civic-site">
      <header className="civic-header">
        <div className="civic-header-inner">
          <button className="civic-brand brand-button" onClick={onBack}>
            <span className="civic-logo">V</span>
            <span className="civic-brand-text">
              <strong>Vijayanagar</strong>
              <small>Civic Portal</small>
            </span>
          </button>

          <button className="login-btn" onClick={onBack}>
            <Home size={16} />
            Home
          </button>
        </div>
      </header>

      <main className="inner-page">
        <div className="content-width">
          <button className="back-link" onClick={onBack}>
            ← Home
          </button>

          <div className="page-heading">
            <span className="eyebrow">VIJAYANAGAR CIVIC CONNECT</span>
            <h1>{title}</h1>
          </div>

          {children}
        </div>
      </main>
    </div>
  );
}

function ResidentProfile({ user, t, lang, onBack }) {
  const [profile, setProfile] = useState({
    name: user?.name || "",
    phone: "",
    email: user?.email || "",
    address: "",
    locality: "",
    ward: "",
    preferred_language: lang === "kn" ? "Kannada" : "English",
    notification_preferences: { in_app: true, email: false, sms: false },
    privacy_acknowledged: false,
  });

  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/me/profile")
      .then((r) => setProfile((p) => ({
        ...p,
        ...r.data,
        notification_preferences: {
          ...p.notification_preferences,
          ...(r.data.notification_preferences || {}),
        },
      })))
      .catch(() => setError("Unable to load profile."));
  }, []);

  const save = async (e) => {
    e.preventDefault();

    try {
      await api.patch("/me/profile", profile);
      setMessage("Profile saved successfully.");
      setError("");
    } catch (err) {
      setError(err.response?.data?.detail || "Unable to save profile.");
    }
  };

  return (
    <SimplePage title={t.profile} onBack={onBack}>
      <form className="profile-card" onSubmit={save}>
        <div className="profile-heading">
          <div className="profile-avatar">
            <UserCircle size={42} />
          </div>
          <div>
            <span className="eyebrow">RESIDENT PROFILE</span>
            <h2>{profile.name || "Resident"}</h2>
            <p>Manage the details used for civic-service communication.</p>
          </div>
        </div>

        <div className="profile-grid">
          <label>
            Name
            <input value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} required />
          </label>

          <label>
            Phone
            <input value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} />
          </label>

          <label className="profile-full">
            Email
            <input value={profile.email} disabled />
          </label>

          <label className="profile-full">
            Address
            <textarea rows="3" value={profile.address} onChange={(e) => setProfile({ ...profile, address: e.target.value })} />
          </label>

          <label>
            Locality
            <input value={profile.locality} onChange={(e) => setProfile({ ...profile, locality: e.target.value })} />
          </label>

          <label>
            Ward
            <select value={profile.ward} onChange={(e) => setProfile({ ...profile, ward: e.target.value })}>
              <option value="">Select ward</option>
              {VIJAYANAGAR_WARDS.map((w) => (
                <option value={w.value || w.number} key={w.number}>
                  Ward {w.number} - {w.name}
                </option>
              ))}
            </select>
          </label>

          <label>
            Preferred language
            <select value={profile.preferred_language} onChange={(e) => setProfile({ ...profile, preferred_language: e.target.value })}>
              <option>English</option>
              <option>Kannada</option>
            </select>
          </label>
        </div>

        <section className="profile-section">
          <h3>Notification Preferences</h3>

          <div className="profile-checks">
            {["in_app", "email", "sms"].map((key) => (
              <label className="profile-check" key={key}>
                <input
                  type="checkbox"
                  checked={!!profile.notification_preferences[key]}
                  onChange={(e) =>
                    setProfile({
                      ...profile,
                      notification_preferences: {
                        ...profile.notification_preferences,
                        [key]: e.target.checked,
                      },
                    })
                  }
                />
                <span>{key === "in_app" ? "In-app" : key.toUpperCase()}</span>
              </label>
            ))}
          </div>
        </section>

        <label className="profile-check">
          <input
            type="checkbox"
            checked={!!profile.privacy_acknowledged}
            onChange={(e) =>
              setProfile({ ...profile, privacy_acknowledged: e.target.checked })
            }
          />
          <span>
            I understand that the information I provide is used for civic-service
            purposes and communication about my submissions.
          </span>
        </label>

        {message && <div className="profile-success">{message}</div>}
        {error && <div className="profile-error">{error}</div>}

        <div className="profile-actions">
          <button type="button" className="civic-btn secondary" onClick={onBack}>
            Home
          </button>
          <button type="submit" className="civic-btn primary">
            Save Profile
          </button>
        </div>
      </form>
    </SimplePage>
  );
}

function MyReports({ user, t, onBack }) {
  const [reports, setReports] = useState([]);
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/complaints/mine"),
      api.get("/notifications/mine"),
    ]).then(([a, b]) => {
      setReports(a.data);
      setNotifications(b.data);
    }).catch(() => {});
  }, []);

  return (
    <SimplePage title={t.myReports} onBack={onBack}>
      {notifications.length > 0 && (
        <section className="civic-data-panel">
          <h2><Bell size={18} /> Notifications</h2>
          {notifications.slice(0, 10).map((n) => (
            <div className="data-row" key={n.id}>
              <span>{n.title}</span>
              <small>{n.message}</small>
            </div>
          ))}
        </section>
      )}

      {reports.length === 0 ? (
        <div className="empty-panel">
          <FileText size={34} />
          <h2>No complaints yet</h2>
        </div>
      ) : (
        reports.map((r) => (
          <article className="resident-report civic-data-panel" key={r.tracking_id}>
            <div className="report-card-head">
              <div>
                <span className="tracking-label">{r.tracking_id}</span>
                <h3>{r.title}</h3>
              </div>
              <span className="status-badge">{r.status}</span>
            </div>

            <p>{r.description}</p>

            <div className="data-row">
              <span>Ward</span>
              <strong>{r.ward}</strong>
            </div>

            <div className="data-row">
              <span>Category</span>
              <strong>{r.category}</strong>
            </div>

            <div className="data-row">
              <span>Priority</span>
              <strong>{r.priority}</strong>
            </div>

            <div className="timeline-list">
              {(r.timeline || []).map((event, i) => (
                <div className="timeline-item" key={i}>
                  <CheckCircle2 size={15} />
                  <div>
                    <strong>{event.status}</strong>
                    <p>{event.note}</p>
                    <small>{event.at}</small>
                  </div>
                </div>
              ))}
            </div>
          </article>
        ))
      )}
    </SimplePage>
  );
}

function ResetPasswordPage({ onBack, go }) {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const params = new URLSearchParams(window.location.search);
  const resetToken = params.get("token") || "";

  const submitReset = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");

    if (!resetToken) {
      setError("This password reset link is invalid or incomplete.");
      return;
    }

    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setSubmitting(true);

    try {
      const r = await api.post("/auth/reset-password", {
        token: resetToken,
        password,
      });

      setMessage(
        r.data?.message || "Your password has been reset successfully."
      );
      setPassword("");
      setConfirmPassword("");
    } catch (err) {
      const detail = err.response?.data?.detail;

      if (typeof detail === "string") {
        setError(detail);
      } else if (Array.isArray(detail)) {
        setError(
          detail
            .map((item) => item?.msg || "Invalid password reset request.")
            .join(" ")
        );
      } else if (detail && typeof detail === "object") {
        setError(detail.msg || "Unable to reset your password.");
      } else {
        setError(
          "Unable to reset your password. The link may have expired."
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="reset-page">
      <div className="reset-card">
        <button
          type="button"
          className="reset-back"
          onClick={onBack}
          aria-label="Go back"
        >
          <ArrowLeft size={18} />
          <span>Back</span>
        </button>

        {message ? (
          <div className="reset-success">
            <div className="reset-icon success">
              <CheckCircle2 size={30} />
            </div>

            <span className="eyebrow">ACCOUNT SECURED</span>

            <h1>Password Reset Complete</h1>

            <p>
              Your password has been changed successfully. You can now sign
              in to your Vijayanagar Civic Portal account using your new
              password.
            </p>

            <button
              type="button"
              className="civic-btn primary full reset-action"
              onClick={() => go("/")}
            >
              Return to Resident Login
            </button>
          </div>
        ) : (
          <>
            <div className="reset-header">
              <div className="reset-icon">
                <LockKeyhole size={30} />
              </div>

              <span className="eyebrow">ACCOUNT SECURITY</span>

              <h1>Create a New Password</h1>

              <p>
                Choose a strong new password to secure your civic portal
                account.
              </p>
            </div>

            <form className="reset-form" onSubmit={submitReset}>
              <label>
                New Password
                <div className="reset-password-field">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    minLength="8"
                    autoComplete="new-password"
                    placeholder="Enter your new password"
                    required
                  />

                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </label>

              <label>
                Confirm New Password
                <div className="reset-password-field">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    minLength="8"
                    autoComplete="new-password"
                    placeholder="Re-enter your new password"
                    required
                  />

                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowConfirmPassword((v) => !v)}
                    aria-label={
                      showConfirmPassword
                        ? "Hide password"
                        : "Show password"
                    }
                  >
                    {showConfirmPassword ? (
                      <EyeOff size={18} />
                    ) : (
                      <Eye size={18} />
                    )}
                  </button>
                </div>
              </label>

              <div className="reset-requirements">
                <div className={password.length >= 8 ? "valid" : ""}>
                  <Check size={15} />
                  At least 8 characters
                </div>

                <div
                  className={
                    password &&
                    confirmPassword &&
                    password === confirmPassword
                      ? "valid"
                      : ""
                  }
                >
                  <Check size={15} />
                  Passwords match
                </div>
              </div>

              {error && (
                <div className="reset-error" role="alert">
                  <AlertCircle size={18} />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                className="civic-btn primary full reset-action"
                disabled={submitting}
              >
                {submitting ? "Updating Password..." : "Reset Password"}
              </button>

              <p className="reset-security-note">
                <ShieldCheck size={16} />
                Your password reset link is temporary and can only be used
                once.
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

function Modal({ type, user, setUser, close, t }) {
  const [mode, setMode] = useState(type);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    title: "",
    category: CIVIC_CATEGORIES[0] || "Other",
    sub_category: "",
    ward: "",
    description: "",
    address: "",
    landmark: "",
    priority: "Normal",
    lat: "",
    lng: "",
    photos: [],
    tracking: "",
  });

  const [result, setResult] = useState(null);
  const [trackResult, setTrackResult] = useState(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotMessage, setForgotMessage] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const update = (key, value) => {
    setForm((p) => ({ ...p, [key]: value }));
    setError("");
  };

  const captureLocation = () => {
    if (!navigator.geolocation) {
      setError("Geolocation is not available.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        update("lat", String(position.coords.latitude));
        update("lng", String(position.coords.longitude));
      },
      () => setError("Unable to capture your location.")
    );
  };

  const submitAuth = async (e) => {
    e.preventDefault();
    setError("");

    if (mode === "register") {
      if (form.password.length < 8) {
        setError("Password must be at least 8 characters.");
        return;
      }

      if (form.password !== confirmPassword) {
        setError("Passwords do not match.");
        return;
      }

      if (!/^\d{10}$/.test(form.phone)) {
        setError("Please enter a valid 10-digit mobile number.");
        return;
      }
    }

    setSubmitting(true);

    try {
      const endpoint =
        mode === "login" ? "/auth/login" : "/auth/register";

      const payload = {
        email: form.email.trim(),
        password: form.password,
      };

      if (mode === "register") {
        payload.name = form.name.trim();
        payload.phone = form.phone.trim();
      }

      const r = await api.post(endpoint, payload);

      setUser(r.data);
      close();

      setConfirmPassword("");
      setShowPassword(false);
      setShowConfirmPassword(false);
    } catch (err) {
      const detail = err.response?.data?.detail;

      if (typeof detail === "string") {
        setError(detail);
      } else if (Array.isArray(detail)) {
        setError(
          detail
            .map((item) => item?.msg || "Please check your information.")
            .join(" ")
        );
      } else if (detail && typeof detail === "object") {
        setError(detail.msg || "Unable to continue.");
      } else {
        setError("Unable to continue. Please check your details.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const submitForgotPassword = async (e) => {
    e.preventDefault();
    setError("");
    setForgotMessage("");

    if (!forgotEmail.trim()) {
      setError("Please enter your email address.");
      return;
    }

    setSubmitting(true);

    try {
      const r = await api.post("/auth/forgot-password", {
        email: forgotEmail.trim(),
      });

      setForgotMessage(
        r.data?.message ||
          "If an account exists for this email, a password reset link has been sent."
      );
    } catch (err) {
      const detail = err.response?.data?.detail;

      if (typeof detail === "string") {
        setError(detail);
      } else if (Array.isArray(detail)) {
        setError(
          detail
            .map((item) => item?.msg || "Invalid email address.")
            .join(" ")
        );
      } else if (detail && typeof detail === "object") {
        setError(detail.msg || "Unable to send reset link.");
      } else {
        setError("Unable to send reset link. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const submitReport = async (e) => {
    e.preventDefault();

    if (!user) {
      setMode("login");
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        title: form.title,
        category: form.category,
        sub_category: form.sub_category,
        ward: form.ward,
        description: form.description,
        address: form.address,
        landmark: form.landmark,
        priority: form.priority,
      };

      if (form.lat && form.lng) {
        payload.latitude = Number(form.lat);
        payload.longitude = Number(form.lng);
      }

      const r = await api.post("/complaints", payload);

      for (const photo of form.photos || []) {
        const fd = new FormData();
        fd.append("photo", photo);
        await api.post(`/complaints/${r.data.tracking_id}/photo`, fd);
      }

      setResult(r.data);
    } catch (err) {
      console.error("Complaint submission error:", err.response?.data);

      const detail = err.response?.data?.detail;

      let message = "Unable to submit complaint.";

      if (typeof detail === "string") {
        message = detail;
      } else if (Array.isArray(detail)) {
        message = detail
          .map((item) => item?.msg || "Invalid complaint information.")
          .join(" ");
      } else if (detail && typeof detail === "object") {
        message = detail.msg || "Some complaint information is invalid.";
      }

      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const track = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setTrackResult(null);

    try {
      const r = await api.get(`/complaints/${form.tracking.trim()}`);
      setTrackResult(r.data);
    } catch (err) {
      setError(err.response?.status === 404 ? t.noResult : "Unable to find complaint.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && close()}>
      <div className="civic-modal civic-modal-wide">
        <button className="modal-close" onClick={close} aria-label={t.close}>
          <X />
        </button>

        {mode === "login" || mode === "register" ? (
          <div className="resident-auth">

            <div className="resident-auth-header">
              <div className="resident-auth-icon">
                {mode === "login" ? (
                  <LogIn size={24} />
                ) : (
                  <UserRound size={24} />
                )}
              </div>

              <div>
                <span className="eyebrow">RESIDENT ACCESS</span>

                <h2>
                  {mode === "login"
                    ? "Welcome back"
                    : "Create your resident account"}
                </h2>

                <p>
                  {mode === "login"
                    ? "Sign in to manage your civic complaints and profile."
                    : "Register once to report and track civic issues."}
                </p>
              </div>
            </div>

            <form onSubmit={submitAuth} className="resident-auth-form">

              {mode === "register" && (
                <label className="auth-field">
                  <span>Your name</span>

                  <div className="auth-input-wrap">
                    <UserRound size={18} />

                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                      autoComplete="name"
                      placeholder="Enter your full name"
                      required
                    />
                  </div>
                </label>
              )}

              <label className="auth-field">
                <span>Email address</span>

                <div className="auth-input-wrap">
                  <Mail size={18} />

                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    autoComplete="email"
                    placeholder="you@example.com"
                    required
                  />
                </div>
              </label>

              {mode === "register" && (
                <label className="auth-field">
                  <span>Mobile Number</span>

                  <div className="auth-input-wrap">
                    <Smartphone size={18} />

                    <input
                      type="tel"
                      inputMode="numeric"
                      autoComplete="tel"
                      placeholder="10-digit mobile number"
                      value={form.phone}
                      onChange={(e) =>
                        update(
                          "phone",
                          e.target.value.replace(/\D/g, "").slice(0, 10)
                        )
                      }
                      maxLength="10"
                      required
                    />
                  </div>
                </label>
              )}

              <label className="auth-field">
                <span>Password</span>

                <div className="auth-input-wrap">
                  <LockKeyhole size={18} />

                  <input
                    type={showPassword ? "text" : "password"}
                    value={form.password}
                    onChange={(e) => update("password", e.target.value)}
                    autoComplete={
                      mode === "login"
                        ? "current-password"
                        : "new-password"
                    }
                    placeholder="Enter your password"
                    minLength="8"
                    required
                  />

                  <button
                    type="button"
                    className="auth-password-toggle"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                  >
                    {showPassword ? (
                      <EyeOff size={18} />
                    ) : (
                      <Eye size={18} />
                    )}
                  </button>
                </div>
              </label>

              {mode === "register" && (
                <>
                  <label className="auth-field">
                    <span>Confirm Password</span>

                    <div
                      className={`auth-input-wrap ${
                        confirmPassword &&
                        form.password !== confirmPassword
                          ? "has-error"
                          : confirmPassword &&
                            form.password === confirmPassword
                          ? "has-success"
                          : ""
                      }`}
                    >
                      <LockKeyhole size={18} />

                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) =>
                          setConfirmPassword(e.target.value)
                        }
                        autoComplete="new-password"
                        placeholder="Re-enter your password"
                        minLength="8"
                        required
                      />

                      <button
                        type="button"
                        className="auth-password-toggle"
                        onClick={() =>
                          setShowConfirmPassword((v) => !v)
                        }
                        aria-label={
                          showConfirmPassword
                            ? "Hide password"
                            : "Show password"
                        }
                      >
                        {showConfirmPassword ? (
                          <EyeOff size={18} />
                        ) : (
                          <Eye size={18} />
                        )}
                      </button>
                    </div>
                  </label>

                  <div className="auth-password-rules">
                    <div className={form.password.length >= 8 ? "valid" : ""}>
                      <Check size={14} />
                      At least 8 characters
                    </div>

                    <div
                      className={
                        form.password &&
                        confirmPassword &&
                        form.password === confirmPassword
                          ? "valid"
                          : ""
                      }
                    >
                      <Check size={14} />
                      Passwords match
                    </div>
                  </div>
                </>
              )}

              {mode === "login" && (
                <div className="auth-forgot-row">
                  <button
                    type="button"
                    className="auth-forgot"
                    onClick={() => {
                      setMode("forgot");
                      setError("");
                      setForgotMessage("");
                      setForgotEmail(form.email || "");
                    }}
                  >
                    Forgot Password?
                  </button>
                </div>
              )}

              {error && (
                <div className="auth-error" role="alert">
                  <AlertCircle size={17} />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                className="civic-btn primary full auth-submit"
                disabled={submitting}
              >
                {submitting
                  ? mode === "login"
                    ? "Signing In..."
                    : "Creating Account..."
                  : mode === "login"
                  ? "Sign In"
                  : "Create Resident Account"}
              </button>
            </form>

            <div className="auth-divider">
              <span />
              <small>
                {mode === "login"
                  ? "New resident?"
                  : "Already registered?"}
              </small>
              <span />
            </div>

            <button
              type="button"
              className="auth-switch-button"
              onClick={() => {
                setMode(mode === "login" ? "register" : "login");
                setError("");
                setConfirmPassword("");
                setShowPassword(false);
                setShowConfirmPassword(false);
              }}
            >
              {mode === "login"
                ? "Create a Resident Account"
                : "Sign in to your account"}
            </button>

            <div className="auth-security-note">
              <ShieldCheck size={16} />

              <span>
                Your account information is used for civic-service access
                and communication about your submissions.
              </span>
            </div>

          </div>
        ) : mode === "forgot" ? (
          <>
            <span className="eyebrow">ACCOUNT RECOVERY</span>
            <h2>Forgot Password?</h2>
            <p>
              Enter your registered email address and, if an account exists,
              we will send you a password reset link.
            </p>

            <form onSubmit={submitForgotPassword}>
              <label>
                Email
                <input
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  autoComplete="email"
                  required
                />
              </label>

              {error && <p className="form-error">{error}</p>}

              {forgotMessage && (
                <div className="profile-success">
                  {forgotMessage}
                </div>
              )}

              <button
                className="civic-btn primary full"
                disabled={submitting}
              >
                {submitting ? "Sending..." : "Send Reset Link"}
              </button>
            </form>

            <button
              type="button"
              className="switch-auth"
              onClick={() => {
                setMode("login");
                setError("");
                setForgotMessage("");
              }}
            >
              Back to Resident Login
            </button>
          </>
        ) : mode === "report" ? (
          result ? (
            <div className="success-panel">
              <CheckCircle2 size={46} />
              <span className="eyebrow">COMPLAINT SUBMITTED</span>
              <h2>{t.success}</h2>

              <div className="tracking-box">
                <small>{t.trackingId}</small>
                <strong>{result.tracking_id}</strong>
              </div>

              <p>
                Ward: <strong>{result.ward}</strong>
              </p>
              <p>
                Category: <strong>{result.category}</strong>
              </p>
              <p>
                Status: <strong>{result.status}</strong>
              </p>

              <button className="civic-btn primary" onClick={close}>
                Done
              </button>
            </div>
          ) : !user ? (
            <div className="signin-required-overlay">
              <div className="signin-required-card">

                <div className="signin-required-icon">
                  <LogIn size={28} strokeWidth={2.2} />
                </div>

                <h2>Sign in to report an issue</h2>

                <p>
                  Please sign in to your resident account before submitting a
                  civic complaint. This helps us keep your complaint details,
                  updates, and tracking information together.
                </p>

                <div className="signin-required-note">
                  <ShieldCheck size={17} />
                  <span>
                    Your account lets you securely track the status and progress
                    of complaints you submit.
                  </span>
                </div>

                <div className="signin-required-actions">
                  <button
                    type="button"
                    className="signin-required-primary"
                    onClick={() => setMode("login")}
                  >
                    Sign In
                  </button>

                  <button
                    type="button"
                    className="signin-required-secondary"
                    onClick={() => setMode("register")}
                  >
                    Create Account
                  </button>
                </div>

              </div>
            </div>
          ) : (
            <>
              <span className="eyebrow">CIVIC ACTION</span>
              <h2>{t.reportTitle}</h2>

              <form onSubmit={submitReport}>
                <div className="profile-grid">
                  <label>
                    {t.title}
                    <input value={form.title} onChange={(e) => update("title", e.target.value)} required />
                  </label>

                  <label>
                    {t.category}
                    <select
                      value={form.category}
                      onChange={(e) => {
                        update("category", e.target.value);
                        update("sub_category", "");
                      }}
                    >
                      {(CIVIC_CATEGORIES.length ? CIVIC_CATEGORIES : Object.keys(subCategories)).map((c) => (
                        <option key={c}>{c}</option>
                      ))}
                    </select>
                  </label>

                  <label>
                    {t.subCategory}
                    <select
                      value={form.sub_category}
                      onChange={(e) => update("sub_category", e.target.value)}
                    >
                      <option value="">Select sub-category</option>
                      {(subCategories[form.category] || ["Other"]).map((x) => (
                        <option key={x}>{x}</option>
                      ))}
                    </select>
                  </label>

                  <label>
                    {t.ward}
                    <select value={form.ward} onChange={(e) => update("ward", e.target.value)} required>
                      <option value="">Select your ward</option>
                      {VIJAYANAGAR_WARDS.map((w) => (
                        <option value={w.value || w.number} key={w.number}>
                          Ward {w.number} - {w.name}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label>
                    {t.priority}
                    <select value={form.priority} onChange={(e) => update("priority", e.target.value)}>
                      <option>Low</option>
                      <option>Normal</option>
                      <option>High</option>
                      <option>Urgent</option>
                    </select>
                  </label>

                  <label className="profile-full">
                    {t.description}
                    <textarea
                      rows="5"
                      value={form.description}
                      onChange={(e) => update("description", e.target.value)}
                      required
                    />
                  </label>

                  <label className="profile-full">
                    {t.address}
                    <textarea
                      rows="2"
                      value={form.address}
                      onChange={(e) => update("address", e.target.value)}
                    />
                  </label>

                  <label>
                    {t.landmark}
                    <input value={form.landmark} onChange={(e) => update("landmark", e.target.value)} />
                  </label>

                  <div className="profile-location-box">
                    <button type="button" className="civic-btn secondary" onClick={captureLocation}>
                      <MapPin size={16} />
                      {t.getLocation}
                    </button>

                    {form.lat && form.lng && (
                      <small>
                        {form.lat}, {form.lng}
                      </small>
                    )}
                  </div>

                  <label className="profile-full">
                    {t.photo}
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      multiple
                      onChange={(e) =>
                        update("photos", Array.from(e.target.files || []))
                      }
                    />
                  </label>
                </div>

                {error && <p className="form-error">{error}</p>}

                <button className="civic-btn primary full" disabled={submitting}>
                  {submitting ? "Submitting..." : t.submit}
                </button>
              </form>
            </>
          )
        ) : (
          <>
            <span className="eyebrow">COMPLAINT TRACKING</span>
            <h2>{t.track}</h2>

            <form onSubmit={track}>
              <label>
                {t.trackingId}
                <input
                  value={form.tracking}
                  onChange={(e) => update("tracking", e.target.value)}
                  placeholder="VJ-CIVIC-2026-XXXXXX"
                  required
                />
              </label>

              {error && <p className="form-error">{error}</p>}

              <button className="civic-btn primary full" disabled={submitting}>
                {submitting ? "Searching..." : "Track Complaint"}
              </button>
            </form>

            {trackResult && (
              <div className="tracking-result">
                <div className="tracking-box">
                  <small>{t.trackingId}</small>
                  <strong>{trackResult.tracking_id}</strong>
                </div>

                <h3>{trackResult.title}</h3>

                <div className="data-row">
                  <span>Ward</span>
                  <strong>{trackResult.ward}</strong>
                </div>

                <div className="data-row">
                  <span>Category</span>
                  <strong>{trackResult.category}</strong>
                </div>

                <div className="data-row">
                  <span>Status</span>
                  <strong>{trackResult.status}</strong>
                </div>

                <div className="data-row">
                  <span>Priority</span>
                  <strong>{trackResult.priority}</strong>
                </div>

                {trackResult.department && (
                  <div className="data-row">
                    <span>Department</span>
                    <strong>{trackResult.department}</strong>
                  </div>
                )}

                {trackResult.expected_resolution_date && (
                  <div className="data-row">
                    <span>Expected resolution</span>
                    <strong>{trackResult.expected_resolution_date}</strong>
                  </div>
                )}

                <h3>{t.timeline}</h3>

                <div className="timeline-list">
                  {(trackResult.timeline || []).map((event, i) => (
                    <div className="timeline-item" key={i}>
                      <CheckCircle2 size={15} />
                      <div>
                        <strong>{event.status}</strong>
                        <p>{event.note}</p>
                        <small>{event.at}</small>
                      </div>
                    </div>
                  ))}
                </div>

                {trackResult.status === "Resolved" && user && (
                  <FeedbackBox trackingId={trackResult.tracking_id} t={t} />
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function FeedbackBox({ trackingId, t }) {
  const [feedback, setFeedback] = useState("");
  const [rating, setRating] = useState(5);
  const [sent, setSent] = useState(false);

  const send = async () => {
    await api.post(`/complaints/${trackingId}/feedback`, {
      feedback,
      rating,
    });
    setSent(true);
  };

  const reopen = async () => {
    await api.post(`/complaints/${trackingId}/reopen`);
    setSent(true);
  };

  if (sent) return <div className="profile-success">Feedback recorded.</div>;

  return (
    <section className="feedback-box">
      <h3>{t.feedback}</h3>
      <p>{t.resolvedQuestion}</p>

      <select value={rating} onChange={(e) => setRating(Number(e.target.value))}>
        <option value="5">5 - Excellent</option>
        <option value="4">4 - Good</option>
        <option value="3">3 - Partial</option>
        <option value="2">2 - Poor</option>
        <option value="1">1 - Not resolved</option>
      </select>

      <textarea
        rows="3"
        value={feedback}
        onChange={(e) => setFeedback(e.target.value)}
        placeholder="Tell us what happened..."
      />

      <div className="hero-buttons">
        <button className="civic-btn primary" onClick={send}>
          Submit Feedback
        </button>
        <button className="civic-btn secondary" onClick={reopen}>
          {t.reopen}
        </button>
      </div>
    </section>
  );
}

function NotFound({ onBack }) {
  return (
    <SimplePage title="Page not found" onBack={onBack}>
      <p>The requested civic page could not be found.</p>
    </SimplePage>
  );
}
